<script setup>
import { ref, watch } from 'vue'
import { Link } from '@inertiajs/vue3'
import { Button } from '@/Components/ui/button'
import { Checkbox } from '@/Components/ui/checkbox'
import { trackEvent } from '@/composables/useAnalytics'
import {
  advertisingAllowed,
  consentBannerOpen,
  consentDecide,
  consentSettingsOpen,
  consentSettingsShow,
  statisticsAllowed,
} from '@/lib/consent'

// What the two switches show until saved, starting from the current answer
const advertisingChoice = ref(advertisingAllowed.value)
const statisticsChoice = ref(statisticsAllowed.value)

watch(consentSettingsOpen, (open) => {
  if (open) {
    advertisingChoice.value = advertisingAllowed.value
    statisticsChoice.value = statisticsAllowed.value
  }
})

// Note: counted before the answer takes effect, which can start the page again, and never from someone turning counting off
function decide(answer) {
  const statistics = answer.statistics ?? statisticsAllowed.value

  if (statistics) {
    trackEvent('consent_answered', { advertising: answer.advertising, statistics })
  }
  consentDecide(answer)
}
</script>

<template>
  <div
    v-if="consentBannerOpen"
    role="dialog"
    aria-labelledby="cookie-consent-title"
    class="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-surface p-4 shadow-lg sm:inset-x-auto sm:bottom-4 sm:left-4 sm:max-w-md sm:rounded-xl sm:border"
  >
    <h2 id="cookie-consent-title" class="font-sans text-base font-bold text-ink">Cookies and analytics</h2>

    <template v-if="!consentSettingsOpen">
      <p class="mt-2 text-sm text-muted">
        We use cookies to see how Bindle is used and which of our adverts work. If you reject them, nothing goes to Google or Meta and we count visits anonymously.
        <button type="button" class="font-medium text-brand underline" @click="consentSettingsShow">Cookie settings</button>
        ·
        <Link href="/privacy-policy" class="font-medium text-brand underline">Privacy policy</Link>
      </p>
      <div class="mt-4 flex gap-3">
        <Button class="flex-1" @click="decide({ advertising: true })">Accept</Button>
        <Button class="flex-1" variant="outline" @click="decide({ advertising: false })">Reject</Button>
      </div>
    </template>

    <template v-else>
      <div class="mt-3 space-y-4">
        <div class="flex items-start gap-3">
          <Checkbox id="consent-advertising" :checked="advertisingChoice" @update:checked="advertisingChoice = $event" />
          <label for="consent-advertising" class="text-sm text-muted">
            <span class="block font-semibold text-ink">Measuring our adverts</span>
            Google Analytics, Google Ads and Meta learn which advert brought you and what you did next, with your email and phone scrambled for Meta; PostHog follows your visit with recordings, anything typed hidden. We build no lists of people to show adverts to.
          </label>
        </div>
        <div class="flex items-start gap-3">
          <Checkbox id="consent-statistics" :checked="statisticsChoice" @update:checked="statisticsChoice = $event" />
          <label for="consent-statistics" class="text-sm text-muted">
            <span class="block font-semibold text-ink">Anonymous statistics</span>
            PostHog counting the pages you visit, where you came from and your device, with nothing stored on your device and nothing that follows you. On unless you turn it off.
          </label>
        </div>
      </div>
      <div class="mt-4 flex gap-3">
        <Button class="flex-1" @click="decide({ advertising: advertisingChoice, statistics: statisticsChoice })">Save</Button>
      </div>
    </template>
  </div>
</template>
